// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ColumnDef, RowSelectionState, VisibilityState } from "@tanstack/react-table";
import { toastError, presentMutationError } from "../../../utils/showApiErrorToast";
import {
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Textarea,
  Text,
  useBreakpointValue,
  useColorModeValue,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import { CircleAlert, Info } from "lucide-react";
import { toast } from "sonner";
import { useHistory, useLocation } from "react-router-dom";
import { useBillingContext } from "../../../contexts/BillingContext";
import {
  formatWeeklyReportQuotaMessage,
  isWeeklyReportQuotaReached,
} from "../../../types/billing";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button as ShadcnButton } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import { createDataTableSelectColumn } from "@/components/ui/data-table-select-column";
import { DataTablePaginationControls } from "@/components/ui/data-table-pagination-controls";
import { TableOutlineDeleteIconButton } from "@/components/ui/table-outline-delete-button";
import {
  StatisticsEmptyState,
  statisticsPageMinHeight,
} from "../Statistics/components/statisticsEmptyState";
import { useActiveBrokerAccount } from "../../../hooks/useActiveBrokerAccount";
import { useUserNotificationTimezone } from "../../../hooks/useUserNotificationTimezone";
import {
  fetchTradeBundle,
  getRecomputeTradeBundleMarketContextJob,
  startRecomputeTradeBundleMarketContextJob,
} from "../../../services/tradeBundlesApi";
import { listBacktests } from "../../../services/backtestsApi";
import { timezoneFromBacktestRow } from "../../../utils/backtestTimezone";
import {
  deleteReport,
  getReportTypes,
  listReportStrategies,
  listReports,
  ReportsApiError,
  updateReportStrategy,
} from "../../../services/reportsApi";
import { fetchReportPolicySimConfig } from "../../../services/tradePolicyApi";
import { DateRange } from "../../../types/trade";
import { useDashboardDateWindowListener } from "../../../hooks/useDashboardDateWindowListener";
import { reportCheckboxSx } from "./reportPageFieldFocus";
import {
  buildPersistedFromReports,
  equalDateRange,
  getInitialDashboardDateWindow,
  presetToLocalDateRange,
  reportsStateFromPersisted,
  writeDashboardDateWindow,
  type DashboardDateWindowV1,
} from "../../../utils/dashboardDateWindowStorage";
import { buildAnalyticsWindowSnakeFields } from "../../../utils/analyticsWindowParams";
import {
  RecomputeMarketContextMetadata,
  RecomputeJobStatus,
  Report,
  ReportListQuery,
  ReportListResponse,
  ReportStrategyResponse,
  ReportTypeOption,
  TradeBundleQuery,
  TradeBundleResponse,
} from "../../../types/reports";
import { getReportRunTimezoneForDisplay, getReportDisplayTitle } from "../../../utils/reportWindowLabel";
import { subscribeReportReady } from "../../../utils/reportReadyEvents";
import { toastReportReadyOnce } from "../../../utils/reportReadyToast";
import {
  extractReportCohortSummary,
  getReportCandleTimeframeForDisplay,
  getReportCohortWindowLabel,
} from "../../../utils/reportListDisplay";
import {
  extractReportFindingTitles,
  formatReportCardCurrency,
} from "../../../utils/reportListCardPreview";
import {
  getReportSymbolTableLabel,
  getReportTypeDisplayLabel,
  mapReportTypeOptionsForDisplay,
  resolveReportTypeDisplayLabel,
} from "../../../utils/reportTypeRules";
import { PageRouteHeadline } from "@/components/Layout/PageRouteHeadline";
import { NeedHelpButton } from "@/components/need-help-button";
import { useShadcnInsetChrome } from "@/contexts/ShadcnInsetChromeContext";
import { useShadcnInsetHeaderActions } from "@/contexts/ShadcnInsetHeaderActionsContext";
import SavedReportsActionBar from "./SavedReportsActionBar";
import { reportsMainCardClassName, reportsPendingCardClassName } from "./reportsCardShell";
import {
  ReportsCardMetaLine,
  getReportTypeBadgeClass,
  getReportTypeBadgeDataType,
} from "./components/ReportsCardChrome";
import {
  readReportsListViewMode,
  writeReportsListViewMode,
  type ReportsListViewMode,
} from "./reportsListViewStorage";
import { ReportsTypeBoard } from "./components/ReportsTypeBoard";
import { ReportsListViewToggle } from "./components/ReportsListViewToggle";
import {
  DeleteReportsDialog,
  type DeleteReportsDialogRequest,
} from "./components/DeleteReportsDialog";
import { formatAccountDisplayLabelShort } from "../../../services/liquidBrokersApi";

type StatusFilter = "all" | "open" | "closed";
type StrategyMode = "none" | "existing" | "new";

/** Always sent with user prompt so generated narratives cite session_name, not sessions/0-style indices. */
const REPORT_SESSION_NAMING_HINT =
  "When you reference rows in stats.session_analysis.sessions (or similar session lists), always name each session using its human-readable session_name (and session_id if helpful)—for example Tokyo, Sydney, London–New York. Do not refer to array positions such as sessions/0 or sessions/1.";

const DEFAULT_PREVIEW_TRADES_LIMIT = 200;
const SAVED_REPORTS_PAGE_SIZE_OPTIONS = [100] as const;
/** Fetch one extra row to detect a next page without guessing from a full page. */
const SAVED_REPORTS_FETCH_EXTRA = 1;
/** When searching, load a larger batch and filter client-side (no list search API yet). */
const SAVED_REPORTS_SEARCH_FETCH_LIMIT = 200;

function matchesSavedReportSearch(report: Report, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;

  const title = getReportDisplayTitle(report).toLowerCase();
  const strategy = (report.strategy?.name ?? "").toLowerCase();
  const symbol = (report.symbol ?? "").toLowerCase();
  const account = (report.broker_account_code ?? "").toLowerCase();
  const type = getReportTypeDisplayLabel(report.report_type).toLowerCase();
  const id = report.id.toLowerCase();
  const reportNumber =
    typeof report.report_sequence_number === "number"
      ? String(report.report_sequence_number)
      : "";

  return (
    title.includes(q) ||
    strategy.includes(q) ||
    symbol.includes(q) ||
    account.includes(q) ||
    type.includes(q) ||
    id.includes(q) ||
    (reportNumber.length > 0 &&
      (`#${reportNumber}`.includes(q) || reportNumber.includes(q)))
  );
}

const REPORTS_STRATEGY_BADGE_CLASS =
  "border-0 bg-transparent font-[family-name:var(--font-primary)] font-medium normal-case tracking-normal text-white shadow-none bg-[image:var(--gradient-primary)]";

const REPORTS_SYMBOL_BADGE_CLASS =
  "border-gray-500 bg-transparent font-[family-name:var(--font-primary)] font-medium normal-case tracking-normal text-gray-500 shadow-none";

const REPORTS_LIST_ALERT_CLASS =
  "w-full rounded-lg font-[family-name:var(--font-primary)]";

const REPORTS_LIST_ALERT_DESC_CLASS =
  "font-[family-name:var(--font-primary)] text-sm font-normal leading-5 text-muted-foreground";

/** Same #d3d3d3 border stroke as meta dividers, horizontal above findings/stats. */
function ReportsCardSectionSeparator() {
  return (
    <Separator
      orientation="horizontal"
      decorative
      data-reports-section-separator=""
      className="mt-3 h-0 w-full shrink-0 border-0 border-t border-solid border-border bg-transparent p-0 shadow-none"
    />
  );
}

function reportStatusBadgeClass(status: string | undefined): string {
  switch (status) {
    case "processing":
      return "border-cyan-500/40 bg-cyan-500/10 text-cyan-700 dark:text-cyan-200";
    case "failed":
      return "border-red-500/40 bg-red-500/10 text-red-700 dark:text-red-200";
    case "draft":
      return "border-amber-500/40 bg-amber-500/10 text-amber-800 dark:text-amber-200";
    default:
      return "";
  }
}

function ReportsCardHeaderRow({
  reportNumber,
  accountCode,
  dateLabel,
  strategyName,
  reportType,
  reportTypeLabel,
  symbol,
  status,
}: {
  reportNumber: number | null | undefined;
  accountCode: string | null | undefined;
  dateLabel: string;
  strategyName?: string | null;
  reportType?: string | null;
  reportTypeLabel: string;
  symbol?: string | null;
  status?: string | null;
}) {
  return (
    <Flex
      align="flex-end"
      justify="space-between"
      gap={3}
      mb={2}
      minW={0}
      w="100%"
      flexShrink={0}
      flexWrap="wrap"
      data-reports-card-meta-row=""
    >
      <Box flex="1" minW={0} mr={2}>
        <ReportsCardMetaLine
          reportNumber={reportNumber}
          accountCode={accountCode}
          dateLabel={dateLabel}
        />
      </Box>
      <Flex
        gap={2}
        flexShrink={0}
        align="flex-end"
        justify="flex-end"
        flexWrap="nowrap"
        alignSelf="flex-end"
        data-reports-card-header-badges=""
      >
        {strategyName ? (
          <Badge
            data-reports-strategy-badge=""
            className={REPORTS_STRATEGY_BADGE_CLASS}
          >
            {strategyName}
          </Badge>
        ) : null}
        <Badge
          data-reports-type-badge=""
          data-report-type={getReportTypeBadgeDataType(reportType || reportTypeLabel)}
          className={getReportTypeBadgeClass(reportType || reportTypeLabel)}
        >
          {reportTypeLabel}
        </Badge>
        {symbol ? (
          <Badge
            variant="outline"
            data-reports-symbol-badge=""
            className={REPORTS_SYMBOL_BADGE_CLASS}
          >
            {symbol}
          </Badge>
        ) : null}
        {status && status !== "published" ? (
          <Badge
            variant="outline"
            data-reports-status-badge=""
            className={reportStatusBadgeClass(status ?? undefined)}
          >
            {status === "processing" ? "Generating…" : status}
          </Badge>
        ) : null}
      </Flex>
    </Flex>
  );
}

function readSavedReportsPageSizeFromStorage(): number {
  return 100;
}
const STRATEGY_DESCRIPTION_SOFT_LIMIT = 3000;
const STRATEGY_NAME_SOFT_LIMIT = 120;
/** Backend trims and caps report title length */
const REPORT_TITLE_MAX = 255;
const PENDING_GENERATED_REPORT_STORAGE_KEY = "reports_pending_generated_report_v1";
const EXPECTED_STATS_SECTIONS: string[] = [
  "aggregated",
  "by_symbol",
  "portfolio_growth",
  "win_rate_trend",
  "pnl_over_time",
  "distribution",
  "risk_metrics",
  "monthly_performance",
  "time_analysis",
  "weekly_breakdown",
  "daily_activity",
  "comparative_analysis",
];

function safeSnippet(text: string | null | undefined, maxLen = 120): string {
  const value = (text || "").trim().replace(/\s+/g, " ");
  if (!value) return "";
  return value.length > maxLen ? `${value.slice(0, maxLen)}…` : value;
}

/** Reports always uses explicit dates; expand dashboard preset into a range when needed. */
function reportsPageDateStateFromPersisted(p: DashboardDateWindowV1): { timeframe: string; dateRange: DateRange } {
  const rs = reportsStateFromPersisted(p);
  if (rs.dateRange.startDate && rs.dateRange.endDate) {
    return { timeframe: rs.timeframe, dateRange: rs.dateRange };
  }
  const r = presetToLocalDateRange(rs.timeframe);
  if (r) return { timeframe: rs.timeframe, dateRange: r };
  return { timeframe: rs.timeframe, dateRange: { startDate: null, endDate: null } };
}

export default function Reports(): JSX.Element {
  const shadcnInset = useShadcnInsetChrome();
  const { setActions: setInsetHeaderActions } = useShadcnInsetHeaderActions();
  const history = useHistory();
  const { status: billingStatus } = useBillingContext();
  const weeklyReportQuotaReached = isWeeklyReportQuotaReached(billingStatus);
  const weeklyReportQuotaMessage = weeklyReportQuotaReached
    ? formatWeeklyReportQuotaMessage(billingStatus)
    : null;
  const location = useLocation<{
    pendingGeneratedReport?: { id: string; title?: string; createdAt?: string };
    clearPendingGeneratedReport?: boolean;
  }>();
  const textColor = useColorModeValue("gray.700", "gray.200");
  const subtleText = useColorModeValue("gray.500", "gray.400");
  // Match Trade Log table PnL colors exactly.
  const pnlPositiveColor = "#06E363";
  const pnlNegativeColor = "#E00101";
  const narrativeSnippetMax = useBreakpointValue({ base: 120, lg: 280 }) ?? 120;
  const reportsCardFontFamily = "var(--font-primary)";

  const { activeAccountCode, accounts, loading: brokerAccountsLoading } = useActiveBrokerAccount();

  const [reportTypeOptions, setReportTypeOptions] = useState<ReportTypeOption[]>([]);
  const [reportTypesLoading, setReportTypesLoading] = useState(false);
  const [strategiesLoading, setStrategiesLoading] = useState(false);
  const [strategyOptions, setStrategyOptions] = useState<ReportStrategyResponse[]>([]);
  const [strategyFilterId, setStrategyFilterId] = useState<string>("");
  const [savedReportsReportTypeFilter, setSavedReportsReportTypeFilter] = useState<string>("");
  const [savedReportsBrokerAccountFilterCode, setSavedReportsBrokerAccountFilterCode] = useState<string>("");
  const [savedReportsSearchQuery, setSavedReportsSearchQuery] = useState("");

  const [reportsLoading, setReportsLoading] = useState(false);
  const [reportsError, setReportsError] = useState<string | null>(null);
  const [reportsResponse, setReportsResponse] = useState<ReportListResponse | null>(null);
  const [reportsHydrated, setReportsHydrated] = useState(false);
  const [savedReportsPage, setSavedReportsPage] = useState(1);
  const [savedReportsPageSize, setSavedReportsPageSize] = useState(readSavedReportsPageSizeFromStorage);
  const [savedReportsKnownLastPage, setSavedReportsKnownLastPage] = useState<number | null>(null);
  const [savedReportsRowSelection, setSavedReportsRowSelection] = useState<RowSelectionState>({});
  const [savedReportsSelectAllFromHeader, setSavedReportsSelectAllFromHeader] = useState(false);
  const [deleteReportsRequest, setDeleteReportsRequest] =
    useState<DeleteReportsDialogRequest | null>(null);
  const [isDeletingReports, setIsDeletingReports] = useState(false);
  const [savedReportsColumnVisibility, setSavedReportsColumnVisibility] =
    useState<VisibilityState>({ id: false, findings: false, narrative: false });
  const [reportTimezoneSupplementalById, setReportTimezoneSupplementalById] = useState<Record<string, string>>({});
  const reportsRequestSeqRef = useRef(0);
  const completedPendingToastIdsRef = useRef<Set<string>>(new Set());
  const [deletingReportId, setDeletingReportId] = useState<string | null>(null);
  const [pendingGeneratedReport, setPendingGeneratedReport] = useState<{
    id: string;
    title: string;
    createdAt: string;
  } | null>(null);
  const [listViewMode, setListViewMode] = useState<ReportsListViewMode>(() =>
    readReportsListViewMode(),
  );

  const effectiveBrokerAccountCode = activeAccountCode || "";

  React.useEffect(() => {
    const search = location.search || "";
    if (!search) return;

    const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
    const qpStrategyId = params.get("strategy_id");

    if (qpStrategyId != null) {
      setStrategyFilterId(qpStrategyId);
    }
  }, [location.search]);

  React.useEffect(() => {
    try {
      const raw = window.localStorage.getItem(PENDING_GENERATED_REPORT_STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as { id?: string; title?: string; createdAt?: string };
      if (!parsed?.id) return;
      setPendingGeneratedReport({
        id: String(parsed.id),
        title: (parsed.title || "New report").trim() || "New report",
        createdAt: parsed.createdAt || new Date().toISOString(),
      });
    } catch {
      // no-op
    }
  }, []);

  React.useEffect(() => {
    try {
      if (!pendingGeneratedReport) {
        window.localStorage.removeItem(PENDING_GENERATED_REPORT_STORAGE_KEY);
      } else {
        window.localStorage.setItem(
          PENDING_GENERATED_REPORT_STORAGE_KEY,
          JSON.stringify(pendingGeneratedReport)
        );
      }
    } catch {
      // no-op
    }
  }, [pendingGeneratedReport]);

  React.useEffect(() => {
    if (location.state?.clearPendingGeneratedReport) {
      setPendingGeneratedReport(null);
      history.replace({ pathname: location.pathname, search: location.search });
      return;
    }

    const pending = location.state?.pendingGeneratedReport;
    if (!pending?.id) return;
    const nextPending = {
      id: pending.id,
      title: (pending.title || "New report").trim() || "New report",
      createdAt: pending.createdAt || new Date().toISOString(),
    };
    setPendingGeneratedReport((current) => {
      if (!current) return nextPending;
      const incomingIsReal = !String(nextPending.id).startsWith("pending-");
      const currentIsReal = !String(current.id).startsWith("pending-");
      if (incomingIsReal || !currentIsReal) return nextPending;
      return current;
    });
    history.replace({ pathname: location.pathname, search: location.search });
  }, [history, location.pathname, location.search, location.state]);

  useEffect(() => {
    let cancelled = false;

    const loadReportTypes = async () => {
      setReportTypesLoading(true);
      try {
        const data = await getReportTypes();
        if (cancelled) return;
        const options = mapReportTypeOptionsForDisplay(
          Array.isArray(data.report_types) ? data.report_types : []
        );
        setReportTypeOptions(options);

      } catch (e) {
        if (cancelled) return;
        presentMutationError(e, "Failed to load report types");
      } finally {
        if (!cancelled) setReportTypesLoading(false);
      }
    };

    void loadReportTypes();
    return () => {
      cancelled = true;
    };
  }, []);

  const reloadStrategies = useCallback(async () => {
    setStrategiesLoading(true);
    try {
      const data = await listReportStrategies({
        include_archived: false,
        limit: 200,
        offset: 0,
        ...(effectiveBrokerAccountCode
          ? { broker_account_code: effectiveBrokerAccountCode }
          : {}),
      });
      const options = Array.isArray(data.strategies) ? data.strategies : [];
      setStrategyOptions(options);
      setStrategyFilterId((prev) => (prev && options.some((s) => s.id === prev) ? prev : ""));
    } catch (e) {
      presentMutationError(e, "Failed to load strategies");
    } finally {
      setStrategiesLoading(false);
    }
  }, [effectiveBrokerAccountCode]);

  useEffect(() => {
    void reloadStrategies();
  }, [reloadStrategies]);

  // Saved reports: filters live in the Saved reports action bar only.
  // Strategy and report type filters live in the Saved reports action bar only.
  const savedReportsOffset = (savedReportsPage - 1) * savedReportsPageSize;
  const savedReportsSearchQueryTrimmed = savedReportsSearchQuery.trim();
  const savedReportsSearchActive = savedReportsSearchQueryTrimmed.length > 0;
  const brokerAccountLabelByCode = useMemo(() => {
    const out = new Map<string, string>();
    for (const acc of accounts || []) {
      if (!acc.accountCode) continue;
      out.set(acc.accountCode, formatAccountDisplayLabelShort(acc));
    }
    return out;
  }, [accounts]);
  const savedReportsBrokerAccountOptions = useMemo(
    () => {
      const seen = new Set<string>();
      const options: Array<{ value: string; label: string }> = [];
      for (const report of reportsResponse?.reports || []) {
        const code = (report.broker_account_code || "").trim();
        if (!code || seen.has(code)) continue;
        seen.add(code);
        options.push({
          value: code,
          label: brokerAccountLabelByCode.get(code) || code,
        });
      }
      return options.sort((a, b) => a.label.localeCompare(b.label));
    },
    [brokerAccountLabelByCode, reportsResponse]
  );
  useEffect(() => {
    if (!savedReportsBrokerAccountFilterCode) return;
    const stillVisible = savedReportsBrokerAccountOptions.some(
      (opt) => opt.value === savedReportsBrokerAccountFilterCode
    );
    if (!stillVisible) {
      setSavedReportsBrokerAccountFilterCode("");
    }
  }, [savedReportsBrokerAccountFilterCode, savedReportsBrokerAccountOptions]);
  const reportListQuery: ReportListQuery = useMemo(
    () => ({
      // Always scope to the active account (or an explicit Saved-reports broker filter).
      broker_account_code:
        savedReportsBrokerAccountFilterCode || effectiveBrokerAccountCode || undefined,
      strategy_id: strategyFilterId || undefined,
      report_type:
        listViewMode === "board"
          ? undefined
          : savedReportsReportTypeFilter || undefined,
      limit: savedReportsSearchActive
        ? SAVED_REPORTS_SEARCH_FETCH_LIMIT
        : savedReportsPageSize + SAVED_REPORTS_FETCH_EXTRA,
      offset: savedReportsSearchActive ? 0 : savedReportsOffset,
    }),
    [
      effectiveBrokerAccountCode,
      savedReportsOffset,
      savedReportsPageSize,
      savedReportsBrokerAccountFilterCode,
      savedReportsReportTypeFilter,
      savedReportsSearchActive,
      strategyFilterId,
      listViewMode,
    ]
  );

  React.useEffect(() => {
    try {
      localStorage.setItem("savedReports_pageSize", String(savedReportsPageSize));
    } catch {
      // no-op
    }
  }, [savedReportsPageSize]);

  React.useEffect(() => {
    setSavedReportsKnownLastPage(null);
    setSavedReportsPage(1);
  }, [
    strategyFilterId,
    savedReportsReportTypeFilter,
    savedReportsBrokerAccountFilterCode,
    savedReportsPageSize,
    savedReportsSearchQueryTrimmed,
    effectiveBrokerAccountCode,
  ]);

  React.useEffect(() => {
    if (!reportsResponse || reportsLoading) return;
    const list = reportsResponse.reports ?? [];
    const len = list.length;
    const page = savedReportsPage;

    if (len === 0 && page > 1) {
      setSavedReportsPage((p) => Math.max(1, p - 1));
      setSavedReportsKnownLastPage(Math.max(1, page - 1));
      return;
    }

    if (len > 0 && len <= savedReportsPageSize) {
      setSavedReportsKnownLastPage(page);
    }
  }, [reportsResponse, reportsLoading, savedReportsPage, savedReportsPageSize]);

  const onSavedReportsStrategyFilterChange = useCallback((strategyId: string) => {
    setStrategyFilterId(strategyId);
  }, []);

  const onSavedReportsReportTypeFilterChange = useCallback((reportTypeKey: string) => {
    setSavedReportsReportTypeFilter(reportTypeKey);
  }, []);

  const handleSavedReportsPageChange = useCallback((page: number) => {
    setSavedReportsPage(page);
    setSavedReportsSelectAllFromHeader(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const savedReportsFilteredRows = useMemo(() => {
    const rows = reportsResponse?.reports ?? [];
    if (!savedReportsSearchActive) return rows;
    return rows.filter((report) =>
      matchesSavedReportSearch(report, savedReportsSearchQueryTrimmed),
    );
  }, [
    reportsResponse?.reports,
    savedReportsSearchActive,
    savedReportsSearchQueryTrimmed,
  ]);

  React.useEffect(() => {
    if (!savedReportsSearchActive) return;
    const totalPages =
      savedReportsFilteredRows.length === 0
        ? 1
        : Math.ceil(savedReportsFilteredRows.length / savedReportsPageSize);
    if (savedReportsPage > totalPages) {
      setSavedReportsPage(totalPages);
    }
  }, [
    savedReportsFilteredRows.length,
    savedReportsPage,
    savedReportsPageSize,
    savedReportsSearchActive,
  ]);

  const savedReportsRawCount = savedReportsSearchActive
    ? savedReportsFilteredRows.length
    : reportsResponse?.reports?.length ?? 0;

  const savedReportsForDisplay = useMemo(() => {
    if (savedReportsSearchActive) {
      const start = (savedReportsPage - 1) * savedReportsPageSize;
      return savedReportsFilteredRows.slice(start, start + savedReportsPageSize);
    }
    return savedReportsFilteredRows.slice(0, savedReportsPageSize);
  }, [
    savedReportsFilteredRows,
    savedReportsPage,
    savedReportsPageSize,
    savedReportsSearchActive,
  ]);
  const savedReportsDisplayCount = savedReportsForDisplay.length;

  React.useEffect(() => {
    if (Object.keys(savedReportsRowSelection).length === 0) {
      setSavedReportsSelectAllFromHeader(false);
    }
  }, [savedReportsRowSelection]);

  const pendingReportVisible = useMemo(
    () =>
      pendingGeneratedReport
        ? savedReportsForDisplay.some((r) => r.id === pendingGeneratedReport.id)
        : false,
    [pendingGeneratedReport, savedReportsForDisplay]
  );

  useEffect(() => {
    const targets = savedReportsForDisplay.filter(
      (report) => !getReportRunTimezoneForDisplay(report) && !reportTimezoneSupplementalById[report.id]
    );
    if (targets.length === 0) return;

    let cancelled = false;

    const resolveSupplementalTimezone = async (report: Report): Promise<string | null> => {
      if (!report?.id) return null;
      try {
        const cfg = await fetchReportPolicySimConfig(report.id);
        const si = cfg?.scope_inputs as Record<string, unknown> | undefined;
        const fromConfig = typeof si?.timezone === "string" ? si.timezone.trim() : "";
        if (fromConfig) return fromConfig;
      } catch {
        // Ignore and try backtest fallback.
      }

      const brokerForBacktest =
        report.broker_account_code ??
        ((report.input_snapshot as { broker_account_code?: string } | null)?.broker_account_code);
      if (!brokerForBacktest) return null;

      try {
        const { backtests } = await listBacktests({
          broker_account_code: brokerForBacktest,
          report_id: report.id,
          limit: 1,
          offset: 0,
        });
        const first = backtests?.[0];
        if (!first) return null;
        return timezoneFromBacktestRow(first);
      } catch {
        return null;
      }
    };

    (async () => {
      const updates: Record<string, string> = {};
      for (const report of targets) {
        const resolved = await resolveSupplementalTimezone(report);
        if (cancelled) return;
        if (resolved) updates[report.id] = resolved;
      }
      if (!cancelled && Object.keys(updates).length > 0) {
        setReportTimezoneSupplementalById((prev) => ({ ...prev, ...updates }));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [reportTimezoneSupplementalById, savedReportsForDisplay]);

  React.useEffect(() => {
    if (!pendingGeneratedReport || !String(pendingGeneratedReport.id).startsWith("pending-")) return;
    const pendingTitle = pendingGeneratedReport.title.trim().toLowerCase();
    if (!pendingTitle) return;
    const pendingCreatedAtMs = new Date(pendingGeneratedReport.createdAt).getTime();
    const match = (reportsResponse?.reports ?? []).find((r) => {
      const reportTitle = (getReportDisplayTitle(r) || "").trim().toLowerCase();
      if (reportTitle !== pendingTitle) return false;
      const reportCreatedAtMs = new Date(r.created_at ?? Date.now()).getTime();
      if (!Number.isFinite(pendingCreatedAtMs) || !Number.isFinite(reportCreatedAtMs)) return true;
      return reportCreatedAtMs >= pendingCreatedAtMs - 10 * 60 * 1000;
    });
    if (!match) return;
    setPendingGeneratedReport({
      id: match.id,
      title: getReportDisplayTitle(match),
      createdAt: match.created_at || pendingGeneratedReport.createdAt,
    });
  }, [pendingGeneratedReport, reportsResponse]);

  React.useEffect(() => {
    if (!pendingGeneratedReport || !pendingReportVisible) return;
    const completedId = pendingGeneratedReport.id;
    if (!completedPendingToastIdsRef.current.has(completedId)) {
      completedPendingToastIdsRef.current.add(completedId);
      toastReportReadyOnce(
        { reportId: completedId, status: "published" },
        { description: "Your generated report is now available in the list." }
      );
    }
    const timeoutId = window.setTimeout(() => {
      setPendingGeneratedReport((current) =>
        current?.id === pendingGeneratedReport.id ? null : current
      );
    }, 600);
    return () => window.clearTimeout(timeoutId);
  }, [pendingGeneratedReport, pendingReportVisible]);

  React.useEffect(() => {
    if (!pendingGeneratedReport) return;
    const timer = window.setInterval(() => {
      void loadReports();
    }, 3000);
    return () => window.clearInterval(timer);
  }, [loadReports, pendingGeneratedReport]);

  const savedReportsHasNextPage = savedReportsSearchActive
    ? savedReportsPage * savedReportsPageSize < savedReportsFilteredRows.length
    : savedReportsKnownLastPage == null && savedReportsRawCount > savedReportsPageSize;

  const savedReportsTotalPages = useMemo(() => {
    if (!reportsResponse) return 0;
    if (savedReportsSearchActive) {
      if (savedReportsFilteredRows.length === 0) return 0;
      return Math.ceil(savedReportsFilteredRows.length / savedReportsPageSize);
    }
    if (savedReportsDisplayCount === 0 && savedReportsPage === 1) return 0;
    // Stable first-page behavior: if page 1 has no next page, total pages is exactly 1.
    if (savedReportsPage === 1 && !savedReportsHasNextPage) return 1;
    if (savedReportsKnownLastPage != null) return savedReportsKnownLastPage;
    if (savedReportsHasNextPage) return savedReportsPage + 1;
    return Math.max(savedReportsPage, 1);
  }, [
    reportsResponse,
    savedReportsDisplayCount,
    savedReportsFilteredRows.length,
    savedReportsPage,
    savedReportsPageSize,
    savedReportsHasNextPage,
    savedReportsKnownLastPage,
    savedReportsSearchActive,
  ]);

  const loadReports = useCallback(async () => {
    const requestSeq = ++reportsRequestSeqRef.current;
    setReportsLoading(true);
    setReportsError(null);
    try {
      const data = await listReports(reportListQuery);
      if (requestSeq !== reportsRequestSeqRef.current) return;
      setReportsResponse(data);
    } catch (e) {
      if (requestSeq !== reportsRequestSeqRef.current) return;
      const msg = e instanceof Error ? e.message : "Failed to load reports.";
      setReportsError(msg);
      // Keep last successful list visible on transient errors.
    } finally {
      if (requestSeq !== reportsRequestSeqRef.current) return;
      setReportsLoading(false);
      setReportsHydrated(true);
    }
  }, [reportListQuery]);

  const openDeleteReportsDialog = useCallback((request: DeleteReportsDialogRequest) => {
    setDeleteReportsRequest(request);
  }, []);

  const closeDeleteReportsDialog = useCallback(
    (open: boolean) => {
      if (!open && !isDeletingReports) {
        setDeleteReportsRequest(null);
      }
    },
    [isDeletingReports]
  );

  const handleConfirmDeleteReports = useCallback(async () => {
    if (!deleteReportsRequest) return;

    const ids =
      deleteReportsRequest.mode === "single"
        ? [deleteReportsRequest.reportId]
        : deleteReportsRequest.reportIds;
    const count = ids.length;

    setIsDeletingReports(true);
    if (deleteReportsRequest.mode === "single") {
      setDeletingReportId(deleteReportsRequest.reportId);
    }

    try {
      await Promise.all(ids.map((id) => deleteReport(id)));
      toast.success(`${count} report${count === 1 ? "" : "s"} deleted`);
      setSavedReportsRowSelection({});
      setSavedReportsSelectAllFromHeader(false);
      setDeleteReportsRequest(null);
      await loadReports();
    } catch (err) {
      toastError(count === 1 ? "Delete failed" : "Bulk delete failed", {
        description:
          err instanceof Error
            ? err.message
            : count === 1
              ? "Failed to delete report."
              : "Failed to delete selected reports.",
      });
    } finally {
      setIsDeletingReports(false);
      setDeletingReportId(null);
    }
  }, [deleteReportsRequest, loadReports]);

  // Auto-load reports on first render and when filters/pagination change.
  // Debounced to avoid hammering API while user types symbol/filter input.
  React.useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      void loadReports();
    }, 250);
    return () => window.clearTimeout(timeoutId);
  }, [loadReports]);

  React.useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") void loadReports();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [loadReports]);

  React.useEffect(() => {
    return subscribeReportReady(() => {
      void loadReports();
    });
  }, [loadReports]);

  const hasInflightReport = useMemo(
    () => (reportsResponse?.reports ?? []).some((r) => r.status === "processing"),
    [reportsResponse?.reports]
  );

  const formatReportTypeLabel = useCallback(
    (reportTypeKey?: string | null): string => {
      const key = String(reportTypeKey ?? "").trim();
      const fromApi = reportTypeOptions.find((o) => o.key === key)?.label;
      return resolveReportTypeDisplayLabel(reportTypeKey, fromApi);
    },
    [reportTypeOptions]
  );

  const reportsSectionGap = "24px";

  const reportTableColumns = useMemo<ColumnDef<Report>[]>(
    () => [
      createDataTableSelectColumn<Report>({
        selectAllFromHeader: savedReportsSelectAllFromHeader,
        onSelectAllFromHeaderChange: setSavedReportsSelectAllFromHeader,
        checkboxDataAttributes: {
          "data-reports-table-checkbox": "",
        },
        headerDataAttributes: {
          "data-reports-select-all-header": "",
        },
      }),
      {
        id: "reportNumber",
        header: "Report #",
        meta: { label: "Report #" },
        cell: ({ row }) =>
          typeof row.original.report_sequence_number === "number"
            ? `#${row.original.report_sequence_number}`
            : "—",
      },
      {
        id: "title",
        header: "Title",
        meta: { label: "Title" },
        cell: ({ row }) => {
          const r = row.original;
          const title = (r.title ?? "").trim()
            ? (r.title ?? "").trim()
            : `${r.report_type || "trade-analysis"} v${r.report_version ?? 1}`;
          return (
            <Text
              data-reports-table-title=""
              color="#38BBD1"
              fontFamily={reportsCardFontFamily}
              fontWeight="medium"
              noOfLines={2}
            >
              {title}
            </Text>
          );
        },
      },
      {
        accessorKey: "created_at",
        header: "Created",
        meta: { label: "Created" },
        cell: ({ row }) => {
          const createdAt = row.original.created_at;
          if (!createdAt) return "—";
          const createdDate = new Date(createdAt);
          if (Number.isNaN(createdDate.getTime())) return "—";
          return createdDate.toLocaleString();
        },
      },
      {
        accessorKey: "broker_account_code",
        header: "Account",
        meta: { label: "Account" },
        cell: ({ row }) => row.original.broker_account_code || "—",
      },
      {
        id: "strategy",
        header: "Strategy",
        meta: { label: "Strategy" },
        cell: ({ row }) => <Text noOfLines={2}>{row.original.strategy?.name || "—"}</Text>,
      },
      {
        id: "type",
        header: "Type",
        meta: { label: "Type" },
        cell: ({ row }) => formatReportTypeLabel(row.original.report_type),
      },
      {
        accessorKey: "symbol",
        header: "Symbol",
        meta: { label: "Symbol" },
        cell: ({ row }) => getReportSymbolTableLabel(row.original.report_type, row.original.symbol),
      },
      {
        id: "window",
        header: "Window",
        meta: { label: "Window" },
        cell: ({ row }) => {
          const reportWindowLabel = getReportCohortWindowLabel(row.original);
          return <Text noOfLines={2}>{reportWindowLabel}</Text>;
        },
      },
      {
        id: "candleTimeframe",
        header: "Timeframe",
        meta: { label: "Timeframe" },
        cell: ({ row }) => getReportCandleTimeframeForDisplay(row.original) || "—",
      },
      {
        id: "timezone",
        header: "Timezone",
        meta: { label: "Timezone" },
        cell: ({ row }) =>
          getReportRunTimezoneForDisplay(row.original) ||
          reportTimezoneSupplementalById[row.original.id] ||
          "—",
      },
      {
        id: "pnl",
        header: "PnL",
        meta: { label: "PnL" },
        cell: ({ row }) => {
          const rangeStats = extractReportCohortSummary(row.original);
          if (!rangeStats) return "—";
          const pnlColor = rangeStats.totalPnl >= 0 ? pnlPositiveColor : pnlNegativeColor;
          return (
            <Text data-trade-log-pnl="" style={{ color: pnlColor }}>
              {formatReportCardCurrency(rangeStats.totalPnl)}
            </Text>
          );
        },
      },
      {
        id: "trades",
        header: "Trades",
        meta: { label: "Trades" },
        cell: ({ row }) => {
          const rangeStats = extractReportCohortSummary(row.original);
          return rangeStats ? rangeStats.totalTrades : "—";
        },
      },
      {
        id: "winRate",
        header: "Win rate",
        meta: { label: "Win rate" },
        cell: ({ row }) => {
          const rangeStats = extractReportCohortSummary(row.original);
          return rangeStats && Number.isFinite(rangeStats.winRate)
            ? `${rangeStats.winRate.toFixed(1)}%`
            : "—";
        },
      },
      {
        id: "findings",
        header: "Findings",
        meta: { label: "Findings" },
        cell: ({ row }) => {
          const findingTitles = extractReportFindingTitles(row.original.findings, 3);
          return (
            <Text color="gray.500" noOfLines={2} title={findingTitles.join(" | ")}>
              {findingTitles.length ? findingTitles.join(" | ") : "—"}
            </Text>
          );
        },
      },
      {
        id: "narrative",
        header: "Narrative",
        meta: { label: "Narrative" },
        cell: ({ row }) => {
          const snippet = safeSnippet(row.original.narrative, 180);
          return (
            <Text color="gray.500" noOfLines={2}>
              {snippet || "—"}
            </Text>
          );
        },
      },
      {
        accessorKey: "id",
        header: "Report ID",
        meta: { label: "Report ID" },
        cell: ({ row }) => (
          <Text noOfLines={1} title={row.original.id}>
            {row.original.id}
          </Text>
        ),
      },
      {
        id: "actions",
        size: 52,
        maxSize: 52,
        enableResizing: false,
        header: "Actions",
        meta: { label: "Actions", align: "end" },
        enableSorting: false,
        enableHiding: false,
        cell: ({ row }) => (
          <Flex justify="flex-end" onClick={(e) => e.stopPropagation()}>
            <TableOutlineDeleteIconButton
              marker="reports"
              aria-label="Delete report"
              loading={deletingReportId === row.original.id}
              disabled={
                isDeletingReports ||
                (deletingReportId != null && deletingReportId !== row.original.id)
              }
              onClick={() => {
                openDeleteReportsDialog({
                  mode: "single",
                  reportId: row.original.id,
                });
              }}
            />
          </Flex>
        ),
      },
    ],
    [
      deletingReportId,
      formatReportTypeLabel,
      isDeletingReports,
      openDeleteReportsDialog,
      pnlNegativeColor,
      pnlPositiveColor,
      reportTimezoneSupplementalById,
      reportsCardFontFamily,
      savedReportsSelectAllFromHeader,
    ]
  );

  const selectedReportIds = useMemo(
    () =>
      Object.entries(savedReportsRowSelection)
        .filter(([, selected]) => selected)
        .map(([id]) => id),
    [savedReportsRowSelection]
  );

  const selectedReportIdsSet = useMemo(
    () => new Set(selectedReportIds),
    [selectedReportIds],
  );

  const selectedReports = useMemo(() => {
    if (selectedReportIds.length === 0) return [];
    const idSet = new Set(selectedReportIds);
    return savedReportsForDisplay.filter((report) => idSet.has(report.id));
  }, [savedReportsForDisplay, selectedReportIds]);

  const openBulkDeleteReportsDialog = useCallback(() => {
    if (selectedReportIds.length === 0) return;
    openDeleteReportsDialog({
      mode: "bulk",
      reportIds: selectedReportIds,
    });
  }, [openDeleteReportsDialog, selectedReportIds]);

  const handleBulkExportSelected = useCallback(() => {
    if (selectedReports.length === 0) return;
    const escapeCsvCell = (value: unknown): string => {
      const text = String(value ?? "");
      if (/[",\n]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
      return text;
    };
    const headers = [
      "report_number",
      "title",
      "created",
      "account",
      "strategy",
      "type",
      "symbol",
      "window",
      "timeframe",
      "timezone",
      "pnl",
      "trades",
      "win_rate",
      "findings",
      "narrative",
      "report_id",
    ];
    const rows = selectedReports.map((report) => {
      const rangeStats = extractReportCohortSummary(report);
      const findingTitles = extractReportFindingTitles(report.findings, 5).join(" | ");
      const title = (report.title ?? "").trim()
        ? (report.title ?? "").trim()
        : `${report.report_type || "trade-analysis"} v${report.report_version ?? 1}`;
      return [
        typeof report.report_sequence_number === "number"
          ? `#${report.report_sequence_number}`
          : "",
        title,
        report.created_at ? new Date(report.created_at).toLocaleString() : "",
        report.broker_account_code || "",
        report.strategy?.name || "",
        formatReportTypeLabel(report.report_type),
        getReportSymbolTableLabel(report.report_type, report.symbol),
        getReportCohortWindowLabel(report),
        getReportCandleTimeframeForDisplay(report) || "",
        getReportRunTimezoneForDisplay(report) || reportTimezoneSupplementalById[report.id] || "",
        rangeStats ? formatReportCardCurrency(rangeStats.totalPnl) : "",
        rangeStats ? rangeStats.totalTrades : "",
        rangeStats && Number.isFinite(rangeStats.winRate)
          ? `${rangeStats.winRate.toFixed(1)}%`
          : "",
        findingTitles,
        safeSnippet(report.narrative, 180),
        report.id,
      ];
    });
    const csv = [headers, ...rows]
      .map((line) => line.map((cell) => escapeCsvCell(cell)).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const filename = `reports-export-${new Date().toISOString().slice(0, 10)}.csv`;
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${selectedReports.length} report${selectedReports.length === 1 ? "" : "s"}`);
  }, [formatReportTypeLabel, reportTimezoneSupplementalById, selectedReports]);

  const selectedToolbarActions =
    selectedReportIds.length > 0 ? (
      <>
        <ShadcnButton variant="outline-brand" size="sm" data-font="primary" className="shrink-0" onClick={handleBulkExportSelected}>
          <span>Export Selected</span>
        </ShadcnButton>
        <ShadcnButton
          variant="outline"
          size="sm"
          data-font="primary"
          data-trade-log-action="delete"
          className="shrink-0 !border-red-500 !text-red-500"
          onClick={openBulkDeleteReportsDialog}
          disabled={isDeletingReports}
        >
          <span>Delete Selected</span>
        </ShadcnButton>
      </>
    ) : null;

  const handleCreateReport = useCallback(() => {
    history.push("/reports/new");
  }, [history]);

  const handleListViewModeChange = useCallback((mode: ReportsListViewMode) => {
    setListViewMode(mode);
    writeReportsListViewMode(mode);
    if (mode === "board") {
      setSavedReportsReportTypeFilter("");
    }
    setSavedReportsRowSelection({});
    setSavedReportsSelectAllFromHeader(false);
  }, []);

  const handleOpenReport = useCallback(
    (report: Report) => {
      history.push({
        pathname: `/reports/${report.id}`,
        state: { reportDisplayTitle: getReportDisplayTitle(report) },
      });
    },
    [history],
  );

  const handleGenerateFromLane = useCallback(
    (path: string) => {
      history.push(path);
    },
    [history],
  );

  const handleBoardReportSelectedChange = useCallback((reportId: string, selected: boolean) => {
    setSavedReportsSelectAllFromHeader(false);
    setSavedReportsRowSelection((prev) => {
      const next = { ...prev };
      if (selected) {
        next[reportId] = true;
      } else {
        delete next[reportId];
      }
      return next;
    });
  }, []);

  const headerGenerateButton = useMemo(
    () => (
      <ShadcnButton
        type="button"
        variant="gradient"
        size="sm"
        data-font="primary"
        className="justify-center"
        onClick={handleCreateReport}
      >
        <span className="truncate normal-case tracking-normal">Generate new report</span>
      </ShadcnButton>
    ),
    [handleCreateReport]
  );

  const reportsViewToggle = useMemo(
    () => (
      <ReportsListViewToggle
        value={listViewMode}
        onChange={handleListViewModeChange}
      />
    ),
    [handleListViewModeChange, listViewMode],
  );

  const toolbarTrailingControls = useMemo(
    () => (
      <>
        {reportsViewToggle}
        {headerGenerateButton}
      </>
    ),
    [headerGenerateButton, reportsViewToggle],
  );

  const reportsHelpHeaderActions = useMemo(
    () => <NeedHelpButton href="https://help.tesseratrades.com/reports" />,
    []
  );

  useEffect(() => {
    if (!shadcnInset) {
      setInsetHeaderActions(null);
      return;
    }
    setInsetHeaderActions(reportsHelpHeaderActions);
    return () => setInsetHeaderActions(null);
  }, [reportsHelpHeaderActions, setInsetHeaderActions, shadcnInset]);

  const savedReportsActionBar = useMemo(
    () => (
      <SavedReportsActionBar
        brokerAccountFilterCode={savedReportsBrokerAccountFilterCode}
        brokerAccountOptions={savedReportsBrokerAccountOptions}
        brokerAccountsLoading={brokerAccountsLoading}
        onBrokerAccountFilterChange={setSavedReportsBrokerAccountFilterCode}
        strategyFilterId={strategyFilterId}
        strategyOptions={strategyOptions}
        strategiesLoading={strategiesLoading}
        onStrategyFilterChange={onSavedReportsStrategyFilterChange}
        reportTypeFilterKey={savedReportsReportTypeFilter}
        reportTypeOptions={reportTypeOptions}
        reportTypesLoading={reportTypesLoading}
        onReportTypeFilterChange={onSavedReportsReportTypeFilterChange}
        searchQuery={savedReportsSearchQuery}
        onSearchQueryChange={setSavedReportsSearchQuery}
        hideReportTypeFilter={listViewMode === "board"}
      />
    ),
    [
      brokerAccountsLoading,
      listViewMode,
      onSavedReportsReportTypeFilterChange,
      onSavedReportsStrategyFilterChange,
      reportTypeOptions,
      reportTypesLoading,
      savedReportsBrokerAccountFilterCode,
      savedReportsBrokerAccountOptions,
      savedReportsReportTypeFilter,
      savedReportsSearchQuery,
      strategiesLoading,
      strategyFilterId,
      strategyOptions,
    ],
  );

  const savedReportsListBody = (() => {
    return (
      <>
        {pendingGeneratedReport && !pendingReportVisible ? (
          <Box
            key={`pending-${pendingGeneratedReport.id}`}
            data-reports-card-chrome=""
            data-pending-generated-report-card=""
            className={reportsPendingCardClassName}
          >
            <Flex justify="space-between" align="center" gap={3} mb={2}>
              <Text
                fontSize={{ base: "xl", lg: "2xl" }}
                data-reports-accent-text=""
                color="#38BBD1"
                fontWeight="normal"
                fontFamily={reportsCardFontFamily}
                lineHeight="1.25"
                noOfLines={1}
                minW={0}
                flex="1"
              >
                {pendingGeneratedReport.title}
              </Text>
              <HStack spacing={2} align="center" flexShrink={0}>
                <Spinner size="sm" color="#38BBD1" thickness="2px" speed="0.7s" />
                <Text
                  data-reports-accent-text=""
                  fontSize="sm"
                  color="#38BBD1"
                  fontFamily={reportsCardFontFamily}
                  lineHeight="1"
                >
                  Generating...
                </Text>
              </HStack>
            </Flex>
            <Text data-reports-muted-text="" fontSize="sm" color="gray.500" fontFamily={reportsCardFontFamily} mb={3}>
              We are generating this report. It will appear in your list automatically when ready.
            </Text>
          </Box>
        ) : null}
        {!reportsResponse ? null : listViewMode === "table" && savedReportsDisplayCount === 0 ? (
          <Text color="gray.500" fontSize="sm" fontFamily={reportsCardFontFamily}>
            No reports match the current filters.
          </Text>
        ) : null}
        {!reportsResponse ? null : listViewMode === "board" ? (
          <div data-reports-list-board="" className="flex w-full min-w-0 flex-col gap-4">
            <div className="flex flex-wrap items-center gap-3">
              {savedReportsActionBar}
              <div className="ml-auto flex shrink-0 flex-wrap items-center justify-end gap-2">
                {selectedToolbarActions}
                {toolbarTrailingControls}
              </div>
            </div>
            <ReportsTypeBoard
              reports={savedReportsForDisplay}
              selectedIds={selectedReportIdsSet}
              onSelectedChange={handleBoardReportSelectedChange}
              onOpenReport={handleOpenReport}
              onDeleteReport={(report) =>
                openDeleteReportsDialog({
                  mode: "single",
                  reportId: report.id,
                })
              }
              deletingReportId={deletingReportId}
              isDeletingReports={isDeletingReports}
              onGenerateClick={handleGenerateFromLane}
            />
            {savedReportsDisplayCount > 0 ? (
              <DataTablePaginationControls
                pageIndex={savedReportsPage - 1}
                pageCount={savedReportsTotalPages}
                onPageIndexChange={(pageIndex) => handleSavedReportsPageChange(pageIndex + 1)}
                canPreviousPage={savedReportsPage > 1}
                canNextPage={
                  savedReportsHasNextPage || savedReportsPage < savedReportsTotalPages
                }
              />
            ) : null}
          </div>
        ) : null}
        {!reportsResponse ? null : listViewMode === "table" && savedReportsDisplayCount > 0 ? (
          <div data-reports-list-table="" className="flex w-full min-w-0 flex-col gap-4">
            <DataTable
              className="dark:text-sidebar-foreground"
              tableClassName="dark:bg-sidebar"
              columns={reportTableColumns}
              data={savedReportsForDisplay}
              getRowId={(row) => row.id}
              pagination={{ pageIndex: 0, pageSize: savedReportsPageSize }}
              enableRowSelection
              rowSelection={savedReportsRowSelection}
              onRowSelectionChange={setSavedReportsRowSelection}
              columnVisibility={savedReportsColumnVisibility}
              onColumnVisibilityChange={setSavedReportsColumnVisibility}
              showColumnVisibility
              columnVisibilityTradeLogTypography
              paginationAfterShell
              toolbarLeading={savedReportsActionBar}
              toolbarActions={selectedToolbarActions}
              toolbarTrailing={toolbarTrailingControls}
              showPagination={false}
              footerTrailing={
                <DataTablePaginationControls
                  pageIndex={savedReportsPage - 1}
                  pageCount={savedReportsTotalPages}
                  onPageIndexChange={(pageIndex) =>
                    handleSavedReportsPageChange(pageIndex + 1)
                  }
                  canPreviousPage={savedReportsPage > 1}
                  canNextPage={
                    savedReportsHasNextPage ||
                    savedReportsPage < savedReportsTotalPages
                  }
                />
              }
              onRowClick={(row) => handleOpenReport(row)}
            />
          </div>
        ) : null}
      </>
    );
  })();

  const showInitialLoading = !reportsError && !reportsHydrated;
  const pageMinHeight = statisticsPageMinHeight(shadcnInset);

  const pageContent = (
    <Flex
      data-reports-page=""
      direction="column"
      pt={shadcnInset ? 0 : { base: "100px", md: "80px", lg: "40px" }}
      pb="24px"
      gap={reportsSectionGap}
      w="100%"
      maxW="100%"
      minW={0}
      minH={pageMinHeight}
    >
      {!shadcnInset ? (
        <Box minW={0}>
          <PageRouteHeadline>Reports</PageRouteHeadline>
        </Box>
      ) : null}

      {showInitialLoading ? (
        <Flex flex="1" minH={0} w="100%" align="center" justify="center">
          <StatisticsEmptyState
            variant="loading"
            fillParent
            title="Loading reports"
            description="Please wait while we load your reports."
          />
        </Flex>
      ) : (
        <div data-reports-main-card="" className={reportsMainCardClassName}>
          {reportsError ? (
            <Alert
              variant="error"
              data-reports-list-alert=""
              className={REPORTS_LIST_ALERT_CLASS}
            >
              <CircleAlert aria-hidden />
              <AlertDescription className={REPORTS_LIST_ALERT_DESC_CLASS}>
                {reportsError}
              </AlertDescription>
            </Alert>
          ) : null}
          {hasInflightReport ? (
            <Alert
              variant="info"
              data-reports-inflight-alert=""
              className={REPORTS_LIST_ALERT_CLASS}
            >
              <Info aria-hidden />
              <AlertDescription className={REPORTS_LIST_ALERT_DESC_CLASS}>
                A report is still generating. This list refreshes automatically when it is ready.
              </AlertDescription>
            </Alert>
          ) : null}
          {weeklyReportQuotaReached ? (
            <Alert
              variant="info"
              data-reports-weekly-quota-alert=""
              className={REPORTS_LIST_ALERT_CLASS}
            >
              <Info aria-hidden />
              <AlertDescription className={REPORTS_LIST_ALERT_DESC_CLASS}>
                {weeklyReportQuotaMessage}
              </AlertDescription>
            </Alert>
          ) : null}
          {savedReportsListBody}
        </div>
      )}
    </Flex>
  ) as JSX.Element;

  return (
    <>
      {pageContent}
      <DeleteReportsDialog
        open={deleteReportsRequest != null}
        onOpenChange={closeDeleteReportsDialog}
        request={deleteReportsRequest}
        isDeleting={isDeletingReports}
        onConfirm={() => void handleConfirmDeleteReports()}
      />
    </>
  );
}

